import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { FaCalendarAlt, FaClock, FaArrowLeft, FaShare, FaLinkedin, FaTwitter, FaEye, FaTag } from 'react-icons/fa';
import { AnimatedSection } from '../components/common';
import { blogsApi } from '../services/api';
import { marked } from 'marked';

// Configure marked options
marked.setOptions({
  breaks: true,
  gfm: true,
});

// Check if content is HTML or Markdown
const isHtml = (content) => {
  return /<[a-z][\s\S]*>/i.test(content) && (
    content.includes('<p>') ||
    content.includes('<h1>') ||
    content.includes('<h2>') ||
    content.includes('<div>')
  );
};

// Blog content component with proper formatting
const BlogContent = ({ content }) => {
  const htmlContent = useMemo(() => {
    if (!content) return '';
    // If content is already HTML, use it directly
    // Otherwise, convert from Markdown
    return isHtml(content) ? content : marked.parse(content);
  }, [content]);

  return (
    <motion.article
      className="bg-white rounded-xl shadow-sm p-8 prose prose-lg max-w-none
        prose-headings:text-dark-800 prose-headings:font-bold
        prose-h1:text-3xl prose-h1:mt-6 prose-h1:mb-4 prose-h1:pb-2 prose-h1:border-b prose-h1:border-gray-200
        prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4 prose-h2:text-primary-700
        prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3
        prose-h4:text-lg prose-h4:mt-4 prose-h4:mb-2
        prose-p:text-dark-600 prose-p:leading-relaxed prose-p:mb-4 prose-p:text-justify
        prose-li:text-dark-600 prose-li:my-1
        prose-ul:my-4 prose-ul:pl-6
        prose-ol:my-4 prose-ol:pl-6
        prose-strong:text-dark-800 prose-strong:font-semibold
        prose-em:text-dark-700
        prose-blockquote:border-l-4 prose-blockquote:border-primary-500 prose-blockquote:pl-4 prose-blockquote:py-2 prose-blockquote:my-4 prose-blockquote:bg-primary-50 prose-blockquote:italic prose-blockquote:text-dark-600 prose-blockquote:rounded-r-lg
        prose-code:bg-gray-100 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm prose-code:font-mono prose-code:text-primary-700
        prose-pre:bg-gray-900 prose-pre:text-gray-100 prose-pre:p-4 prose-pre:rounded-lg prose-pre:overflow-x-auto prose-pre:my-4
        prose-a:text-primary-500 prose-a:underline prose-a:hover:text-primary-600
        prose-img:rounded-lg prose-img:shadow-md prose-img:my-4
        prose-hr:my-8 prose-hr:border-gray-200"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      dangerouslySetInnerHTML={{ __html: htmlContent }}
    />
  );
};

// Calculate read time from content
const calculateReadTime = (content) => {
  if (!content) return '3 min read';
  const text = content.replace(/<[^>]*>/g, ''); // Strip HTML tags
  const words = text.split(/\s+/).length;
  const minutes = Math.ceil(words / 200);
  return `${minutes} min read`;
};

const BlogPost = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchBlog = useCallback(async () => {
    try {
      const response = await blogsApi.getBySlug(slug);
      if (response.success && response.data?.blog) {
        const blogData = response.data.blog;
        // Transform to match frontend expectations
        setBlog({
          ...blogData,
          createdAt: blogData.published_at || blogData.created_at,
          featuredImage: blogData.featured_image,
          readTime: calculateReadTime(blogData.content),
          authorName: blogData.author_name || 'Rakesh Kumar Tripathi',
          category: blogData.category,
          categoryName: blogData.category_name
        });
      } else {
        setError('Blog post not found');
      }
    } catch (err) {
      console.error('Failed to fetch blog:', err);
      setError('Failed to load blog post');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchBlog();
  }, [fetchBlog]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-dark-800 mb-4">Blog Not Found</h1>
          <p className="text-dark-500 mb-6">{error || 'The blog post you are looking for does not exist.'}</p>
          <Link
            to="/blogs"
            className="inline-flex items-center space-x-2 px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            <FaArrowLeft />
            <span>Back to Blogs</span>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{blog.meta_title || blog.title} | Rakesh Kumar Tripathi</title>
        <meta name="description" content={blog.meta_description || blog.excerpt} />
        <meta property="og:title" content={blog.meta_title || blog.title} />
        <meta property="og:description" content={blog.meta_description || blog.excerpt} />
        <meta property="og:type" content="article" />
        {blog.featuredImage && (
          <meta property="og:image" content={blog.featuredImage.startsWith('http') ? blog.featuredImage : `${window.location.origin}${blog.featuredImage}`} />
        )}
      </Helmet>

      {/* Hero Section */}
      <section className="pt-8 pb-16 gradient-bg-subtle">
        <div className="container-custom">
          <AnimatedSection>
            <div className="max-w-4xl mx-auto">
              {/* Back Link */}
              <Link
                to="/blogs"
                className="inline-flex items-center space-x-2 text-dark-500 hover:text-primary-500 transition-colors mb-6"
              >
                <FaArrowLeft />
                <span>Back to Blogs</span>
              </Link>

              {/* Category Badge */}
              {blog.categoryName && (
                <Link
                  to={`/blogs?category=${blog.category}`}
                  className="inline-block px-3 py-1 text-sm font-medium rounded-full bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors mb-4"
                >
                  <FaTag className="inline w-3 h-3 mr-1" />
                  {blog.categoryName}
                </Link>
              )}

              {/* Title */}
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-dark-800 mb-6">
                {blog.title}
              </h1>

              {/* Meta */}
              <div className="flex flex-wrap items-center gap-6 text-dark-500">
                <span className="flex items-center">
                  <FaCalendarAlt className="w-4 h-4 mr-2" />
                  {formatDate(blog.createdAt)}
                </span>
                <span className="flex items-center">
                  <FaClock className="w-4 h-4 mr-2" />
                  {blog.readTime}
                </span>
                {blog.views > 0 && (
                  <span className="flex items-center">
                    <FaEye className="w-4 h-4 mr-2" />
                    {blog.views} views
                  </span>
                )}
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Featured Image */}
      {blog.featuredImage && (
        <section className="pb-12">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto">
              <img
                src={blog.featuredImage}
                alt={blog.title}
                className="w-full h-auto rounded-xl shadow-lg"
              />
            </div>
          </div>
        </section>
      )}

      {/* Content */}
      <section className="pb-16">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="grid lg:grid-cols-12 gap-8">
              {/* Main Content */}
              <div className="lg:col-span-9">
                <BlogContent content={blog.content} />
              </div>

              {/* Sidebar */}
              <div className="lg:col-span-3">
                <div className="sticky top-24 space-y-6">
                  {/* Share */}
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <h3 className="font-bold text-dark-800 mb-4 flex items-center">
                      <FaShare className="mr-2" />
                      Share
                    </h3>
                    <div className="flex space-x-3">
                      <a
                        href={`https://twitter.com/intent/tweet?url=${shareUrl}&text=${blog.title}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-10 h-10 bg-blue-400 text-white rounded-lg flex items-center justify-center hover:bg-blue-500 transition-colors"
                      >
                        <FaTwitter />
                      </a>
                      <a
                        href={`https://www.linkedin.com/shareArticle?mini=true&url=${shareUrl}&title=${blog.title}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-10 h-10 bg-blue-700 text-white rounded-lg flex items-center justify-center hover:bg-blue-800 transition-colors"
                      >
                        <FaLinkedin />
                      </a>
                    </div>
                  </div>

                  {/* Author */}
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <h3 className="font-bold text-dark-800 mb-4">Author</h3>
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center text-white font-bold">
                        {blog.authorName ? blog.authorName.split(' ').map(n => n[0]).join('').slice(0, 2) : 'RK'}
                      </div>
                      <div>
                        <p className="font-medium text-dark-800">{blog.authorName || 'Rakesh Kumar Tripathi'}</p>
                        <p className="text-sm text-dark-500">Cyber Security & IT Expert</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 bg-primary-50">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-2xl font-bold text-dark-800 mb-4">Enjoyed this article?</h2>
            <p className="text-dark-500 mb-6">Check out more insights on cyber security and IT infrastructure.</p>
            <Link
              to="/blogs"
              className="inline-flex items-center space-x-2 px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
            >
              <span>View All Articles</span>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default BlogPost;
